import { Layout } from '@app/app-main/widgets';
import { useRoleBasedAccess, useUserContextSelector, useModuleVisibility } from '@app/main/utils';
import { useMemo } from 'react';
import { Navigate, Outlet, RouteObject, createBrowserRouter } from 'react-router-dom';
import { Error404Page } from './Misc.Errors.404/Error404Page';
import { UserChangePasswordPage } from './Users.ChangePassword/UserChangePasswordPage';
import { routes } from './routes';
import { usePatientDetailsModuleRoute } from './usePatientDetailsModuleRoute';
import { useAdministrationModuleRoute } from './useAdministrationModuleRoute';
import { useEncounterModuleRoute } from './useEncounterModuleRoute';
import { usePatientHealthModuleRoute } from './usePatientHealthModuleRoute';
import { array } from '@app/base';

export function useRouter() {
  const [isPatient, isGlobalAdmin, isPerformer] = useUserContextSelector((x) => [
    x.hasRole('PATIENT'),
    x.hasRole('GLOBAL_ADMIN'),
    x.hasRole('PERFORMER'),
  ]);

  const roleAccessMap = useRoleBasedAccess();
  const { isSchedulingVisible } = useModuleVisibility();
  const patientDetailsRoute = usePatientDetailsModuleRoute();
  const administrationRoute = useAdministrationModuleRoute();
  const encounterModuleRoute = useEncounterModuleRoute();
  const patientHealthModuleRoute = usePatientHealthModuleRoute();

  return useMemo(() => {
    const BASE_PAGES: RouteObject[] = [
      {
        path: '/terms-and-conditions',
        lazy: () =>
          import('./Misc.Terms/TermsPage').then(({ TermsPage }) => ({
            Component: TermsPage,
          })),
      },
      {
        path: '/about-us',
        lazy: () =>
          import('./Misc.AboutUs/AboutUsPage').then(({ AboutUsPage }) => ({
            Component: AboutUsPage,
          })),
      },
      {
        path: '*',
        element: <Error404Page />,
      },
    ];

    const ORGANIZATION_PAGES: RouteObject[] = isGlobalAdmin
      ? [
          {
            path: '/organizations',
            lazy: () =>
              import('./Organizations.List/OrganizationsListPage').then(({ OrganizationsListPage }) => ({
                Component: OrganizationsListPage,
              })),
          },
          {
            path: '/organizations/create',
            lazy: () =>
              import('./Organizations.Create/CreateOrganizationPage').then(({ CreateOrganizationPage }) => ({
                Component: CreateOrganizationPage,
              })),
          },
        ]
      : [];

    const getHomeUrl = () => {
      if (isPatient) return '/dashboard';
      if (roleAccessMap.DashboardViewRoles) return '/dashboard';
      return 'me';
    };

    return createBrowserRouter([
      {
        index: true,
        element: <Navigate to={getHomeUrl()} replace />,
      },

      {
        path: '/',
        element: (
          <Layout>
            <Outlet />
          </Layout>
        ),
        children: routes([
          ...BASE_PAGES,
          administrationRoute,
          roleAccessMap.PatientListAccessRoles && patientDetailsRoute,
          roleAccessMap.EncounterManagementRoles && encounterModuleRoute,
          patientHealthModuleRoute,
          ...ORGANIZATION_PAGES,
          roleAccessMap.DashboardViewRoles && {
            path: '/dashboard',
            lazy: () =>
              import('./Dashboard/DashboardPage').then(({ DashboardPage }) => ({
                Component: DashboardPage,
              })),
          },
          roleAccessMap.ChatAccessRoles && {
            path: '/chat',
            lazy: () =>
              import('./Chat/ChatPage').then(({ ChatPage }) => ({
                Component: ChatPage,
              })),
          },
          roleAccessMap.ChatAccessRoles && {
            path: 'chat/:id',
            lazy: () =>
              import('./PatientProviderChat/PatientProviderChatPage').then(({ PatientProviderChatPage }) => ({
                Component: PatientProviderChatPage,
              })),
          },
          {
            path: '/change-password',
            element: <UserChangePasswordPage />,
          },
          roleAccessMap.PatientListAccessRoles && {
            path: '/patient',
            lazy: () =>
              import('./Patients.List/PatientsListPage').then(({ PatientsListPage }) => ({
                Component: PatientsListPage,
              })),
          },
          roleAccessMap.PatientManagementRoles && {
            path: '/patient/add',
            lazy: () =>
              import('./Patients.Add/PatientAddPage').then(({ PatientAddPage }) => ({
                Component: PatientAddPage,
              })),
          },
          {
            path: '/consent/:consentId',
            lazy: () =>
              import('./Consents.Details/ConsentsDetailsPage').then(({ ConsentsDetailsPage }) => ({
                Component: ConsentsDetailsPage,
              })),
          },
          isPatient && {
            path: '/patient/:patientId/consent/:consentId',
            lazy: () =>
              import('./PatientPortal.Consents.Details/PatientPortalConsentDetailsPage').then(
                ({ PatientPortalConsentDetailsPage }) => ({
                  Component: PatientPortalConsentDetailsPage,
                }),
              ),
          },
          isSchedulingVisible &&
            roleAccessMap.ScheduleViewAccessRoles && {
              path: '/appointment/schedule',
              lazy: () =>
                import('./Appointments.Schedule/AppointmentsSchedulePage').then(
                  ({ AppointmentsSchedulePage }) => ({
                    Component: AppointmentsSchedulePage,
                  }),
                ),
            },
          {
            path: '/appointment/agenda',
            lazy: () =>
              import('./Appointments.Agenda/AppointmentsAgendaPage').then(({ AppointmentsAgendaPage }) => ({
                Component: AppointmentsAgendaPage,
              })),
          },
          roleAccessMap.EncounterReportAccessRoles && {
            path: '/report',
            lazy: () =>
              import('./Encounters.Report/EncountersReportListPage').then(({ EncountersReportListPage }) => ({
                Component: EncountersReportListPage,
              })),
          },
          {
            path: '/notifications',
            lazy: () =>
              import('./Notifications.List/NotificationsListPage').then(({ NotificationsListPage }) => ({
                Component: NotificationsListPage,
              })),
          },
          isPatient && {
            path: '/me',
            lazy: () =>
              import('./Patients.Profile.Module/PatientProfilePageModule').then(
                ({ PatientProfilePageModule }) => ({
                  Component: PatientProfilePageModule,
                }),
              ),
            children: [
              {
                index: true,
                lazy: () =>
                  import('./Patients.Profile/PatientProfilePage').then(({ PatientProfilePage }) => ({
                    Component: PatientProfilePage,
                  })),
              },
              {
                path: 'appearance',
                lazy: () =>
                  import('./Appearance/AppearancePage').then(({ AppearancePage }) => ({
                    Component: AppearancePage,
                  })),
              },
            ],
          },
          !isPatient && {
            path: '/me',
            lazy: () =>
              import('./Employees.Profile.Module/EmployeeProfilePageModule').then(
                ({ EmployeeProfilePageModule }) => ({
                  Component: EmployeeProfilePageModule,
                }),
              ),
            children: array.withoutDisabled([
              {
                index: true,
                lazy: () =>
                  import('./Employee.Profile/EmployeeProfilePage').then(({ EmployeeProfilePage }) => ({
                    Component: EmployeeProfilePage,
                  })),
              },
              isSchedulingVisible &&
                isPerformer && {
                  path: 'availability',
                  lazy: () =>
                    import('./Providers.Availability/ProviderAvailabilityPage').then(
                      ({ ProviderAvailabilityPage }) => ({
                        Component: ProviderAvailabilityPage,
                      }),
                    ),
                },
              {
                path: 'appearance',
                lazy: () =>
                  import('./Appearance/AppearancePage').then(({ AppearancePage: UserPreferencesPage }) => ({
                    Component: UserPreferencesPage,
                  })),
              },
            ]),
          },
          ...routes([
            isPatient && {
              path: '/appointment/agenda',
              lazy: () =>
                import('./Appointments.Agenda/AppointmentsAgendaPage').then(({ AppointmentsAgendaPage }) => ({
                  Component: AppointmentsAgendaPage,
                })),
            },
            isSchedulingVisible &&
              roleAccessMap.ScheduleViewAccessRoles && {
                path: '/appointment/schedule',
                lazy: () =>
                  import('./Appointments.Schedule/AppointmentsSchedulePage').then(
                    ({ AppointmentsSchedulePage }) => ({
                      Component: AppointmentsSchedulePage,
                    }),
                  ),
              },
          ]),
        ]),
      },
    ]);
  }, [
    isGlobalAdmin,
    administrationRoute,
    roleAccessMap.PatientListAccessRoles,
    roleAccessMap.EncounterManagementRoles,
    roleAccessMap.EncounterReportAccessRoles,
    roleAccessMap.DashboardViewRoles,
    roleAccessMap.ChatAccessRoles,
    roleAccessMap.PatientManagementRoles,
    roleAccessMap.ScheduleViewAccessRoles,
    patientDetailsRoute,
    encounterModuleRoute,
    patientHealthModuleRoute,
    isPatient,
    isSchedulingVisible,
    isPerformer,
  ]);
}
